import type { AppRouter } from "@sp-crm/server/rpc";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCContext } from "@trpc/tanstack-react-query";
import React from "react";
import { ClientHttpError } from "util/http";

const trpcContext = createTRPCContext<AppRouter>();
export const trpc = trpcContext;

async function trpcFetch(
    url: URL | RequestInfo,
    options?: RequestInit,
): Promise<Response> {
    const res = await fetch(url, {
        ...options,
        credentials: "include",
    });

    const contentType = res.headers.get("content-type") || "";
    const isJsonResponse = contentType.includes("application/json");

    if (!isJsonResponse) {
        const responseBody = await res.text();
        throw new ClientHttpError(
            "trpc",
            res.status,
            res.statusText,
            responseBody.slice(0, 500),
        );
    }

    return res;
}

export const trpcClient = createTRPCClient<AppRouter>({
    links: [
        httpBatchLink({
            url: "/api/trpc",
            fetch: trpcFetch,
        }),
    ],
});

function makeQueryClient() {
    return new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 0,
                refetchOnWindowFocus: true,
                refetchOnMount: true,
                refetchOnReconnect: true,
            },
        },
    });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
    if (typeof window === "undefined") {
        // Server: always make a new query client
        return makeQueryClient();
    } else {
        // Browser: make a new query client if we don't already have one
        // This is very important, so we don't re-make a new client if React
        // suspends during the initial render. This may not be needed if we
        // have a suspense boundary BELOW the creation of the query client
        if (!browserQueryClient) browserQueryClient = makeQueryClient();
        return browserQueryClient;
    }
}

export const TrpcApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const queryClient = getQueryClient();

    return (
        <trpc.TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </trpc.TRPCProvider>
    );
};
